import '@ionic/react/css/core.css'

import { Capacitor } from '@capacitor/core'
import { IonContent, IonModal, ModalOptions } from '@ionic/react'
import { GlobalStyles, useTheme } from '@mui/material'
import { forwardRef, PropsWithChildren, useEffect } from 'react'
import { isIOS } from 'react-device-detect'

export type BottomSheetProps = PropsWithChildren<{
  open: boolean
  animated?: boolean
  onClose: () => void
  breakpoints: ModalOptions['breakpoints']
  initialBreakpoint: ModalOptions['initialBreakpoint']
  handle?: boolean
  backdropDismiss?: boolean
  swipeToClose?: boolean
  canDismiss?: boolean
}>

export const BottomSheet = forwardRef<HTMLIonModalElement, BottomSheetProps>(
  (
    {
      open,
      onClose,
      animated,
      breakpoints,
      initialBreakpoint,
      handle,
      backdropDismiss = true,
      swipeToClose,
      canDismiss,
      children,
    },
    ref
  ) => {
    const theme = useTheme()
    const isNative = Capacitor.isNativePlatform()

    const styles = {
      'ion-content': {
        '--overflow': 'scroll',
      },
      'ion-content::part(scroll)': {
        backgroundColor: theme.palette?.background?.default,
      },
      'ion-modal::part(content)': {
        maxWidth: 600,
      },
      'ion-modal': {
        position: 'fixed',
      } as never,

      /**
       * @hack
       * 見た目はそのまま
       * アクションシートのスワイプ可能領域サイズを大きくする
       */
      '::part(handle)': {
        opacity: handle ? '1' : 0,
        backgroundColor: 'initial',
        width: 'calc(100% - 92px)',
        marginTop: 8,
        height: 30,
        outline: 0,
        WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
        '&::before': {
          background: 'var(--ion-color-step-350, #d0d0ce)',
          borderRadius: 8,
          height: 0,
          padding: '2.7px 3.3px',
        },
      },
    }

    useEffect(() => {
      if (!document) return
      if (open) {
        document.body.style.overflowY = 'hidden'
        return
      }
      if (isIOS && isNative) {
        document.body.style.overflowY = 'unset'
        return
      }
      document.body.style.overflowY = 'auto'

      return () => {
        document.body.style.overflowY = 'auto'
      }
    }, [open])

    return (
      <>
        <GlobalStyles styles={styles} />
        <IonModal
          ref={ref}
          isOpen={open}
          mode="ios"
          animated={animated}
          onDidDismiss={onClose}
          breakpoints={breakpoints}
          initialBreakpoint={initialBreakpoint}
          handle={handle}
          swipeToClose={swipeToClose}
          canDismiss={canDismiss}
          backdropDismiss={backdropDismiss}
        >
          <IonContent>{children}</IonContent>
        </IonModal>
      </>
    )
  }
)
